import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import CurrencySelect from "./CurrencySelect";
import { Checkbox } from "./ui/checkbox";
import { CLIENT_SERVICES, SOCIAL_SLOTS, PM_SLOT, servicesOf, teamPlan, canSeeCosts, billingOf } from "../lib/services";
import { useAuth } from "../context/AuthContext";
import { formatMoney, currencySymbol } from "../lib/currency";

const EMPTY = {
  client_name:"", brand_name:"", industry:"", contact_person:"", email:"", phone:"", website:"",
  instagram:"", facebook:"", linkedin:"", youtube:"", other_platforms:"",
  account_manager:"",
  services:[], service_retainers:{}, notify_new_members:true,
  assigned_account_manager:"", assigned_graphic_designer:"", assigned_content_writer:"", assigned_execution_specialist:"",
  assigned_project_manager:"", assigned_developers:[],
  contract_start:"", contract_end:"",
  monthly_retainer:0, currency:"USD", package:"Standard", status:"Active", brand_color:"",
};

const BRAND_COLORS = [
  { hex: "", label: "Default (Orange)" },
  { hex: "#F97316", label: "Orange" },
  { hex: "#EF4444", label: "Red" },
  { hex: "#8B5CF6", label: "Violet" },
  { hex: "#3B82F6", label: "Blue" },
  { hex: "#10B981", label: "Emerald" },
  { hex: "#EC4899", label: "Pink" },
  { hex: "#F59E0B", label: "Amber" },
  { hex: "#0EA5E9", label: "Sky" },
  { hex: "#14B8A6", label: "Teal" },
  { hex: "#0F172A", label: "Slate" },
];

const INDUSTRIES = ["Tech SaaS","Luxury Apparel","Fitness & Wellness","Artisan Coffee","Fintech","Healthcare","Real Estate","Hospitality","Food & Beverage","Beauty","Automotive","Education","Other"];
const PACKAGES = ["Starter","Standard","Growth","Premium","Enterprise"];
const STATUS = ["Active","Onboarding","Paused","Ended"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;
const contactErrors = (f) => {
  const e = {};
  if (f.email?.trim() && !EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  const digits = (f.phone || "").replace(/\D/g, "").length;
  if (f.phone?.trim() && (!PHONE_RE.test(f.phone.trim()) || digits < 7 || digits > 15)) e.phone = "Please enter a valid phone number (7–15 digits).";
  return e;
};


export default function ClientForm({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const { user: me } = useAuth();
  const showCosts = canSeeCosts(me);

  useEffect(()=>{ setForm(initial ? {...EMPTY, ...initial, services: servicesOf(initial), assigned_developers: initial.assigned_developers || [], service_retainers: initial.service_retainers || {}, service_billing: Object.fromEntries(servicesOf(initial).map(s => [s, billingOf(initial, s)]))} : EMPTY); }, [initial, open]);
  useEffect(()=>{ (async ()=>{
    if (!open) return;
    const { data } = await api.get("/users");
    setUsers(data);
  })(); }, [open]);

  const set = (k,v) => { setForm(f => ({...f, [k]: v})); setErrors(e => (e[k] ? { ...e, [k]: undefined } : e)); };
  const toggleIn = (k, v) => setForm(f => ({...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v]}));
  const plan = teamPlan(form.services);
  const splitValues = form.services.map(s => form.service_retainers?.[s]).filter(v => v !== undefined && v !== "");
  const hasSplit = splitValues.length > 0;
  const billOf = (s) => (form.service_billing?.[s] === "one_time" ? "one_time" : "monthly");
  const sumBy = (type) => form.services.filter(s => billOf(s) === type).reduce((a, s) => a + (parseFloat(form.service_retainers?.[s]) || 0), 0);
  const splitTotal = sumBy("monthly");
  const oneTimeTotal = sumBy("one_time");
  const setRetainer = (s, v) => setForm(f => ({...f, service_retainers: {...(f.service_retainers || {}), [s]: v}}));
  const setBilling = (s, v) => setForm(f => ({...f, service_billing: {...(f.service_billing || {}), [s]: v}}));

  const save = async () => {
    if (!form.services.length) { toast.error("Pick at least one service signed with this client."); return; }
    const errs = contactErrors(form);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(Object.values(errs)[0]); return; }
    setBusy(true);
    try {
      const { last_activity_at, archived_at, created_at, emailed, one_time_total, ...rest } = form;
      const payload = {...rest, monthly_retainer: parseFloat(form.monthly_retainer)||0};
      if (form.services.some(s => { const v = form.service_retainers?.[s]; return v !== undefined && v !== "" && parseFloat(v) < 0; })) { toast.error("Retainer amounts cannot be negative."); setBusy(false); return; }
      payload.service_billing = Object.fromEntries(form.services.map(s => [s, billOf(s)]));
      if (hasSplit) {
        payload.service_retainers = Object.fromEntries(form.services.map(s => [s, parseFloat(form.service_retainers?.[s]) || 0]));
        payload.monthly_retainer = splitTotal;
      } else {
        payload.service_retainers = {};
      }
      if (!showCosts) { delete payload.service_retainers; delete payload.service_billing; delete payload.monthly_retainer; }
      // clear team slots that no longer apply to the selected services
      SOCIAL_SLOTS.forEach(s => { if (!plan.slots.includes(s)) payload[s.field] = ""; });
      if (!plan.dev) { payload.assigned_project_manager = ""; payload.assigned_developers = []; }
      // auto-fill legacy account_manager string name from selected user for backward compat
      const am = users.find(u=>u.id===payload.assigned_account_manager);
      if (am) payload.account_manager = am.name;
      const res = initial?.id ? await api.put(`/clients/${initial.id}`, payload) : await api.post("/clients", payload);
      const sent = res.data?.emailed || 0;
      toast.success(`${initial?.id ? "Client updated" : "Client created"}${sent ? ` · welcome email sent to ${sent} team member${sent === 1 ? "" : "s"}` : ""}`);
      const before = new Set([...SOCIAL_SLOTS.map(s => initial?.[s.field]), initial?.assigned_project_manager, ...(initial?.assigned_developers || [])].filter(Boolean));
      const now = [...SOCIAL_SLOTS.map(s => payload[s.field]), payload.assigned_project_manager, ...(payload.assigned_developers || [])].filter(Boolean);
      if (payload.notify_new_members && !sent && now.some(x => !before.has(x))) toast.warning("Welcome emails could not be sent right now. The team assignment is saved.");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const byRole = (role) => users.filter(u => u.role === role);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{initial?.id ? "Edit Client" : "New Client"}</DialogTitle>
        </DialogHeader>

        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mt-2">Client Details</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Client Name" required><Input value={form.client_name} onChange={e=>set("client_name", e.target.value)} data-testid="cf-client-name" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Brand Name"><Input value={form.brand_name} onChange={e=>set("brand_name", e.target.value)} data-testid="cf-brand-name" className="bg-[#FFFFFF] border-[#E4E7EC]"/></Field>
          <Field label="Industry">
            <Select value={form.industry} onValueChange={v=>set("industry", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="cf-industry"><SelectValue placeholder="Pick industry"/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{INDUSTRIES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Contact Person"><Input value={form.contact_person} onChange={e=>set("contact_person", e.target.value)} data-testid="cf-contact"/></Field>
          <Field label="Email"><Input type="email" placeholder="name@company.com" value={form.email} onChange={e=>set("email", e.target.value)} data-testid="cf-email"/>{errors.email && <div className="text-[11px] text-rose-600" data-testid="cf-email-error">{errors.email}</div>}</Field>
          <Field label="Phone"><Input type="tel" inputMode="tel" placeholder="+91 98765 43210" value={form.phone} onChange={e=>set("phone", e.target.value.replace(/[^0-9+\-()\s]/g, ""))} data-testid="cf-phone"/>{errors.phone && <div className="text-[11px] text-rose-600" data-testid="cf-phone-error">{errors.phone}</div>}</Field>
          <Field label="Website"><Input value={form.website} onChange={e=>set("website", e.target.value)} data-testid="cf-website"/></Field>
          <Field label="Instagram"><Input value={form.instagram} onChange={e=>set("instagram", e.target.value)} data-testid="cf-instagram"/></Field>
          <Field label="Facebook"><Input value={form.facebook} onChange={e=>set("facebook", e.target.value)} data-testid="cf-facebook"/></Field>
          <Field label="LinkedIn"><Input value={form.linkedin} onChange={e=>set("linkedin", e.target.value)} data-testid="cf-linkedin"/></Field>
          <Field label="YouTube"><Input value={form.youtube} onChange={e=>set("youtube", e.target.value)} data-testid="cf-youtube"/></Field>
          <Field label="Other Platforms"><Input value={form.other_platforms} onChange={e=>set("other_platforms", e.target.value)} data-testid="cf-other-platforms"/></Field>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mt-4">Services Signed<span className="text-rose-500 ml-0.5">*</span></div>
        <div className="flex flex-wrap gap-2" data-testid="cf-services">
          {CLIENT_SERVICES.map(s => {
            const on = form.services.includes(s);
            return (
              <button key={s} type="button" onClick={()=>toggleIn("services", s)} data-testid={`cf-service-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${on ? "bg-orange-500 border-orange-500 text-white font-semibold" : "border-[#E4E7EC] text-neutral-700 hover:border-orange-400"}`}>
                {s}
              </button>
            );
          })}
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mt-4">Assigned Team</div>
        {!form.services.length && <div className="text-xs text-neutral-500" data-testid="cf-team-hint">Pick the services above to see which team members to assign.</div>}
        <div className="grid md:grid-cols-2 gap-4">
          {[...plan.slots, ...(plan.dev ? [PM_SLOT] : [])].map((rf) => (
            <Field key={rf.field} label={rf.label}>
              <Select value={form[rf.field] || ""} onValueChange={v=>set(rf.field, v==="__none"?"":v)}>
                <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid={rf.testid}>
                  <SelectValue placeholder="Unassigned"/>
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                  <SelectItem value="__none">Unassigned</SelectItem>
                  {byRole(rf.role).map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          ))}
        </div>
        {plan.dev && (
          <Field label={`Developers (${form.assigned_developers.length})`}>
            <div className="rounded-lg border border-[#E4E7EC] max-h-44 overflow-y-auto divide-y divide-[#E4E7EC]" data-testid="cf-developers">
              {byRole("developer").length === 0 && <div className="px-3 py-2 text-[11px] text-neutral-500">No developers in the team yet</div>}
              {byRole("developer").map(u => (
                <label key={u.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-neutral-50" data-testid={`cf-dev-${u.id}`}>
                  <Checkbox checked={form.assigned_developers.includes(u.id)} onCheckedChange={()=>toggleIn("assigned_developers", u.id)}/>
                  <span className="text-sm">{u.name}</span>
                  <span className="text-[10px] font-mono text-neutral-500 ml-auto">{u.email}</span>
                </label>
              ))}
            </div>
          </Field>
        )}
        {form.services.length > 0 && (
          <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-[#E4E7EC] bg-[#F5F7FB]" data-testid="cf-notify-toggle">
            <Checkbox checked={form.notify_new_members} onCheckedChange={(v)=>set("notify_new_members", !!v)} className="mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Email newly assigned team members</div>
              <div className="text-[11px] text-neutral-600">Each new member gets a welcome email with the client's services and their role. Existing members are not re-emailed.</div>
            </div>
          </label>
        )}

        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mt-4">Contract</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Package">
            <Select value={form.package} onValueChange={v=>set("package", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="cf-package"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{PACKAGES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={v=>set("status", v)}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="cf-status"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{STATUS.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Contract Start"><Input type="date" value={form.contract_start} onChange={e=>set("contract_start", e.target.value)} data-testid="cf-start"/></Field>
          <Field label="Contract End"><Input type="date" value={form.contract_end} onChange={e=>set("contract_end", e.target.value)} data-testid="cf-end"/></Field>
          <Field label="Currency">
            <CurrencySelect value={form.currency} onChange={v=>set("currency", v)} testid="cf-currency"/>
          </Field>
          {showCosts && (
          <Field label="Monthly Retainer (total)">
            {hasSplit
              ? <div className="h-9 px-3 flex items-center rounded-md border border-[#E4E7EC] bg-[#F5F7FB] text-sm font-semibold" data-testid="cf-retainer-total">{formatMoney(splitTotal, form.currency)}<span className="text-xs text-neutral-500 ml-1">/mo</span>{oneTimeTotal > 0 && <span className="ml-auto text-xs text-neutral-600" data-testid="cf-onetime-total">+ {formatMoney(oneTimeTotal, form.currency)} one-time</span>}</div>
              : <Input type="number" min="0" value={form.monthly_retainer} onChange={e=>set("monthly_retainer", e.target.value)} data-testid="cf-retainer"/>}
          </Field>
          )}
        </div>

        {showCosts && form.services.length > 0 && (
          <div className="space-y-2" data-testid="cf-service-retainers">
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mt-4">Cost per Service</div>
            <div className="grid md:grid-cols-2 gap-3">
              {form.services.map(s => {
                const key = s.toLowerCase().replace(/\s+/g, "-");
                const b = billOf(s);
                return (
                  <Field key={s} label={s}>
                    <div className="flex gap-2">
                      <Input type="number" min="0" step="any" placeholder={`${currencySymbol(form.currency)} ${b === "monthly" ? "per month" : "one time"}`} value={form.service_retainers?.[s] ?? ""} onChange={e=>setRetainer(s, e.target.value)} data-testid={`cf-retainer-${key}`}/>
                      <div className="flex rounded-md border border-[#E4E7EC] overflow-hidden shrink-0" data-testid={`cf-billing-${key}`}>
                        {[["monthly", "Monthly"], ["one_time", "One-time"]].map(([v, l]) => (
                          <button key={v} type="button" onClick={()=>setBilling(s, v)} data-testid={`cf-billing-${key}-${v}`}
                            className={`px-2.5 text-[11px] transition-colors ${b === v ? "bg-orange-500 text-white font-semibold" : "bg-white text-neutral-600 hover:bg-orange-50"}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                  </Field>
                );
              })}
            </div>
            <div className="text-[11px] text-neutral-500">
              {hasSplit ? "Monthly total = sum of monthly services. One-time payments are tracked separately." : "Fill these in to see what each service earns. Until then the total retainer above is used."}
            </div>
          </div>
        )}

        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mt-4">Brand Accent</div>
        <div className="flex items-center gap-3 flex-wrap">
          {BRAND_COLORS.map(c => (
            <button
              key={c.hex || "default"}
              type="button"
              onClick={()=>set("brand_color", c.hex)}
              data-testid={`cf-brand-color-${c.hex || "default"}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-all ${form.brand_color === c.hex ? "border-neutral-900 shadow-sm" : "border-neutral-200 hover:border-neutral-400"}`}
            >
              <span className="w-4 h-4 rounded-full border border-neutral-300" style={c.hex ? { background: c.hex } : { background: "linear-gradient(135deg, #F97316, #EF4444)" }} />
              {c.label}
            </button>
          ))}
        </div>
        <div className="text-[10px] font-mono text-neutral-500 mt-1">Applied on the client's own portal for a personalised look.</div>

        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="cf-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !form.client_name} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="cf-save">
            {busy ? "Saving..." : "Save Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({label, required, children}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">{label}{required && <span className="text-rose-400 ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}
